import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Mail, Newspaper, Mic, Award,
  Image, FileText, LogOut, ChevronRight, Trash2, Plus,
  Eye, Edit2, X, Loader2, Search, ArrowLeft, Upload, ImageIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
};

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "registrations", label: "Registrations", icon: Users },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "newsletter", label: "Newsletter", icon: Newspaper },
  { id: "speakers", label: "Speakers", icon: Mic },
  { id: "sponsors", label: "Sponsors", icon: Award },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "blog", label: "Blog", icon: FileText },
];

// --- Modal Component ---
const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// --- Overview Tab ---
const OverviewTab = ({ stats }) => {
  const statCards = [
    { label: "Registrations", value: stats?.registrations || 0, color: "text-teal-400" },
    { label: "Contacts", value: stats?.contacts || 0, color: "text-cyan-400" },
    { label: "Unread Messages", value: stats?.unread_contacts || 0, color: "text-amber-400" },
    { label: "Newsletter Subs", value: stats?.newsletter || 0, color: "text-purple-400" },
    { label: "Speakers", value: stats?.speakers || 0, color: "text-teal-400" },
    { label: "Sponsors", value: stats?.sponsors || 0, color: "text-cyan-400" },
    { label: "Gallery Items", value: stats?.gallery || 0, color: "text-purple-400" },
    { label: "Blog Posts", value: stats?.blog || 0, color: "text-amber-400" },
  ];
  return (
    <div>
      <h2 className="text-2xl font-extrabold tracking-tighter text-white mb-6">Dashboard Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-stats">
        {statCards.map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-5" data-testid={`stat-card-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`text-3xl font-extrabold tracking-tighter ${s.color}`}>{s.value}</div>
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mt-1 font-medium">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Data Table ---
const DataTable = ({ columns, data, onDelete, onAction, actionLabel, actionIcon, searchKey }) => {
  const [search, setSearch] = useState("");
  const filtered = searchKey
    ? data.filter((r) => (r[searchKey] || "").toLowerCase().includes(search.toLowerCase()))
    : data;

  return (
    <div>
      {searchKey && (
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full bg-zinc-900 border border-zinc-800 pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            data-testid="table-search"
          />
        </div>
      )}
      <div className="overflow-x-auto border border-zinc-800">
        <table className="w-full text-sm" data-testid="data-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/50">
              {columns.map((col) => (
                <th key={col.key} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">
                  {col.label}
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-zinc-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-zinc-500">No data found</td></tr>
            ) : (
              filtered.map((row, i) => (
                <tr key={row.id || i} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3 text-zinc-300 max-w-[200px] truncate">
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {onAction && (
                        <button onClick={() => onAction(row)} className="text-teal-400 hover:text-teal-300 p-1" data-testid={`action-btn-${row.id}`}>
                          {actionIcon || <Eye size={14} />}
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(row.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-btn-${row.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-xs text-zinc-500">{filtered.length} of {data.length} items</div>
    </div>
  );
};

// --- Registrations Tab ---
const RegistrationsTab = ({ data, refresh }) => {
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    await axios.delete(`${API}/admin/registrations/${id}`, getAuthHeaders());
    toast.success("Registration deleted");
    refresh();
  };
  return (
    <div>
      <h2 className="text-2xl font-extrabold tracking-tighter text-white mb-6">Registrations</h2>
      <DataTable
        columns={[
          { key: "full_name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "organization", label: "Organization" },
          { key: "created_at", label: "Date", render: (v) => new Date(v).toLocaleDateString() },
        ]}
        data={data}
        onDelete={handleDelete}
        searchKey="full_name"
      />
    </div>
  );
};

// --- Contacts Tab ---
const ContactsTab = ({ data, refresh }) => {
  const [viewing, setViewing] = useState(null);
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders());
    toast.success("Contact deleted");
    refresh();
  };
  const handleRead = async (row) => {
    setViewing(row);
    if (!row.read) {
      await axios.put(`${API}/admin/contacts/${row.id}/read`, {}, getAuthHeaders());
      refresh();
    }
  };
  return (
    <div>
      <h2 className="text-2xl font-extrabold tracking-tighter text-white mb-6">Contact Messages</h2>
      <DataTable
        columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "subject", label: "Subject" },
          { key: "read", label: "Status", render: (v) => <span className={v ? "text-zinc-500" : "text-teal-400 font-semibold"}>{ v ? "Read" : "New"}</span> },
          { key: "created_at", label: "Date", render: (v) => new Date(v).toLocaleDateString() },
        ]}
        data={data}
        onDelete={handleDelete}
        onAction={handleRead}
        searchKey="name"
      />
      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.subject || "Message"}>
        {viewing && (
          <div className="space-y-4 text-sm">
            <div><span className="text-zinc-500">From:</span> <span className="text-white ml-2">{viewing.name} ({viewing.email})</span></div>
            {viewing.phone && <div><span className="text-zinc-500">Phone:</span> <span className="text-white ml-2">{viewing.phone}</span></div>}
            <div className="border-t border-zinc-800 pt-4 text-zinc-300 leading-relaxed whitespace-pre-wrap">{viewing.message}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// --- Newsletter Tab ---
const NewsletterTab = ({ data, refresh }) => {
  const handleDelete = async (id) => {
    if (!window.confirm("Remove subscriber?")) return;
    await axios.delete(`${API}/admin/newsletter/${id}`, getAuthHeaders());
    toast.success("Subscriber removed");
    refresh();
  };
  return (
    <div>
      <h2 className="text-2xl font-extrabold tracking-tighter text-white mb-6">Newsletter Subscribers</h2>
      <DataTable
        columns={[
          { key: "email", label: "Email" },
          { key: "subscribed_at", label: "Subscribed", render: (v) => new Date(v).toLocaleDateString() },
        ]}
        data={data}
        onDelete={handleDelete}
        searchKey="email"
      />
    </div>
  );
};

// --- CRUD Form Helper ---
const FormField = ({ label, name, value, onChange, type = "text", placeholder, required }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">{label}{required && " *"}</label>
    {type === "textarea" ? (
      <textarea name={name} value={value} onChange={onChange} rows={3} placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none" />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
    )}
  </div>
);

// --- Image Upload Component ---
const ImageUploadField = ({ label, value, onChange, required }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File too large. Max 5MB.");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Invalid file type. Use JPG, PNG, GIF, WebP, or SVG.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(fullUrl);
      toast.success("Image uploaded successfully");
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">
        {label}{required && " *"}
      </label>

      {/* Preview */}
      {value && (
        <div className="mb-3 relative inline-block">
          <img src={value} alt="Preview" className="w-24 h-24 object-cover border border-zinc-800 rounded-sm" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
            data-testid="remove-image-btn"
          >
            <X size={10} />
          </button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed p-4 text-center transition-colors cursor-pointer ${
          dragOver ? "border-teal-500 bg-teal-500/5" : "border-zinc-700 hover:border-zinc-600"
        }`}
        onClick={() => document.getElementById(`file-input-${label}`).click()}
        data-testid="image-upload-zone"
      >
        <input
          id={`file-input-${label}`}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 size={18} className="animate-spin text-teal-400" />
            <span className="text-zinc-400 text-sm">Uploading...</span>
          </div>
        ) : (
          <div className="py-2">
            <Upload size={20} className="mx-auto text-zinc-500 mb-2" />
            <p className="text-zinc-400 text-sm">Click or drag image here</p>
            <p className="text-zinc-600 text-xs mt-1">JPG, PNG, GIF, WebP, SVG &bull; Max 5MB</p>
          </div>
        )}
      </div>

      {/* OR divider */}
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 h-px bg-zinc-800" />
        <span className="text-zinc-600 text-xs uppercase tracking-wider">or paste URL</span>
        <div className="flex-1 h-px bg-zinc-800" />
      </div>

      {/* URL input */}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://example.com/image.jpg"
        className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
        data-testid="image-url-input"
      />
    </div>
  );
};

// --- Speakers Tab ---
const SpeakersTab = ({ data, refresh }) => {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", bio: "", image: "" });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ name: "", role: "", bio: "", image: "" }); setModal("new"); };
  const openEdit = (item) => { setForm({ name: item.name, role: item.role, bio: item.bio, image: item.image }); setModal(item.id); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "new") {
        await axios.post(`${API}/admin/speakers`, form, getAuthHeaders());
        toast.success("Speaker added");
      } else {
        await axios.put(`${API}/admin/speakers/${modal}`, form, getAuthHeaders());
        toast.success("Speaker updated");
      }
      setModal(null);
      refresh();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this speaker?")) return;
    await axios.delete(`${API}/admin/speakers/${id}`, getAuthHeaders());
    toast.success("Speaker deleted");
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold tracking-tighter text-white">Speakers</h2>
        <button onClick={openNew} className="bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-speaker-btn">
          <Plus size={16} /> Add Speaker
        </button>
      </div>
      <DataTable
        columns={[
          { key: "image", label: "Photo", render: (v) => <img src={v} alt="" className="w-10 h-10 object-cover rounded-sm" /> },
          { key: "name", label: "Name" },
          { key: "role", label: "Role" },
        ]}
        data={data}
        onDelete={handleDelete}
        onAction={openEdit}
        actionIcon={<Edit2 size={14} />}
        searchKey="name"
      />
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "new" ? "Add Speaker" : "Edit Speaker"}>
        <div className="space-y-4">
          <FormField label="Name" name="name" value={form.name} onChange={handleChange} required />
          <FormField label="Role / Title" name="role" value={form.role} onChange={handleChange} required />
          <FormField label="Bio" name="bio" value={form.bio} onChange={handleChange} type="textarea" />
          <ImageUploadField label="Speaker Photo" value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          <button onClick={handleSave} disabled={saving || !form.name || !form.role}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-speaker-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {modal === "new" ? "Add Speaker" : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// --- Sponsors Tab ---
const SponsorsTab = ({ data, refresh }) => {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", tier: "silver" });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ name: "", tier: "silver" }); setModal("new"); };
  const openEdit = (item) => { setForm({ name: item.name, tier: item.tier }); setModal(item.id); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "new") {
        await axios.post(`${API}/admin/sponsors`, form, getAuthHeaders());
        toast.success("Sponsor added");
      } else {
        await axios.put(`${API}/admin/sponsors/${modal}`, form, getAuthHeaders());
        toast.success("Sponsor updated");
      }
      setModal(null);
      refresh();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sponsor?")) return;
    await axios.delete(`${API}/admin/sponsors/${id}`, getAuthHeaders());
    toast.success("Sponsor deleted");
    refresh();
  };

  const tierColors = { platinum: "text-cyan-300", gold: "text-amber-400", silver: "text-zinc-300", bronze: "text-orange-400" };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold tracking-tighter text-white">Sponsors</h2>
        <button onClick={openNew} className="bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-sponsor-btn">
          <Plus size={16} /> Add Sponsor
        </button>
      </div>
      <DataTable
        columns={[
          { key: "name", label: "Name" },
          { key: "tier", label: "Tier", render: (v) => <span className={`uppercase text-xs font-semibold tracking-wider ${tierColors[v] || ''}`}>{v}</span> },
        ]}
        data={data}
        onDelete={handleDelete}
        onAction={openEdit}
        actionIcon={<Edit2 size={14} />}
        searchKey="name"
      />
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "new" ? "Add Sponsor" : "Edit Sponsor"}>
        <div className="space-y-4">
          <FormField label="Name" name="name" value={form.name} onChange={handleChange} required />
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">Tier *</label>
            <select name="tier" value={form.tier} onChange={handleChange}
              className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500">
              <option value="platinum">Platinum</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="bronze">Bronze</option>
            </select>
          </div>
          <button onClick={handleSave} disabled={saving || !form.name}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-sponsor-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {modal === "new" ? "Add Sponsor" : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// --- Gallery Tab ---
const GalleryTab = ({ data, refresh }) => {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ url: "", caption: "", year: "" });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ url: "", caption: "", year: new Date().getFullYear().toString() }); setModal("new"); };
  const openEdit = (item) => { setForm({ url: item.url, caption: item.caption, year: item.year }); setModal(item.id); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "new") {
        await axios.post(`${API}/admin/gallery`, form, getAuthHeaders());
        toast.success("Gallery item added");
      } else {
        await axios.put(`${API}/admin/gallery/${modal}`, form, getAuthHeaders());
        toast.success("Gallery item updated");
      }
      setModal(null);
      refresh();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    await axios.delete(`${API}/admin/gallery/${id}`, getAuthHeaders());
    toast.success("Gallery item deleted");
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold tracking-tighter text-white">Gallery</h2>
        <button onClick={openNew} className="bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-gallery-btn">
          <Plus size={16} /> Add Image
        </button>
      </div>
      <DataTable
        columns={[
          { key: "url", label: "Preview", render: (v) => <img src={v} alt="" className="w-16 h-10 object-cover rounded-sm" /> },
          { key: "caption", label: "Caption" },
          { key: "year", label: "Year" },
        ]}
        data={data}
        onDelete={handleDelete}
        onAction={openEdit}
        actionIcon={<Edit2 size={14} />}
        searchKey="caption"
      />
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "new" ? "Add Gallery Image" : "Edit Gallery Image"}>
        <div className="space-y-4">
          <ImageUploadField label="Gallery Image" value={form.url} onChange={(url) => setForm({ ...form, url })} required />
          <FormField label="Caption" name="caption" value={form.caption} onChange={handleChange} required />
          <FormField label="Year" name="year" value={form.year} onChange={handleChange} required />
          <button onClick={handleSave} disabled={saving || !form.url || !form.caption}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-gallery-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {modal === "new" ? "Add Image" : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// --- Blog Tab ---
const BlogTab = ({ data, refresh }) => {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", date: "", author: "", category: "Events", content: "" });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ title: "", excerpt: "", date: new Date().toISOString().split("T")[0], author: "", category: "Events", content: "" }); setModal("new"); };
  const openEdit = (item) => { setForm({ title: item.title, excerpt: item.excerpt, date: item.date, author: item.author, category: item.category, content: item.content || "" }); setModal(item.id); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "new") {
        await axios.post(`${API}/admin/blog`, form, getAuthHeaders());
        toast.success("Blog post added");
      } else {
        await axios.put(`${API}/admin/blog/${modal}`, form, getAuthHeaders());
        toast.success("Blog post updated");
      }
      setModal(null);
      refresh();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    await axios.delete(`${API}/admin/blog/${id}`, getAuthHeaders());
    toast.success("Blog post deleted");
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold tracking-tighter text-white">Blog Posts</h2>
        <button onClick={openNew} className="bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-blog-btn">
          <Plus size={16} /> New Post
        </button>
      </div>
      <DataTable
        columns={[
          { key: "title", label: "Title" },
          { key: "category", label: "Category" },
          { key: "author", label: "Author" },
          { key: "date", label: "Date" },
        ]}
        data={data}
        onDelete={handleDelete}
        onAction={openEdit}
        actionIcon={<Edit2 size={14} />}
        searchKey="title"
      />
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === "new" ? "New Blog Post" : "Edit Blog Post"}>
        <div className="space-y-4">
          <FormField label="Title" name="title" value={form.title} onChange={handleChange} required />
          <FormField label="Excerpt" name="excerpt" value={form.excerpt} onChange={handleChange} type="textarea" required />
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Author" name="author" value={form.author} onChange={handleChange} required />
            <FormField label="Date" name="date" value={form.date} onChange={handleChange} type="date" required />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-medium block mb-2">Category *</label>
            <select name="category" value={form.category} onChange={handleChange}
              className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500">
              <option value="Events">Events</option>
              <option value="Impact">Impact</option>
              <option value="Announcements">Announcements</option>
              <option value="Technology">Technology</option>
              <option value="Community">Community</option>
            </select>
          </div>
          <FormField label="Full Content" name="content" value={form.content} onChange={handleChange} type="textarea" />
          <button onClick={handleSave} disabled={saving || !form.title || !form.excerpt}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-blog-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {modal === "new" ? "Publish Post" : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// --- Main Dashboard ---
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [blog, setBlog] = useState([]);

  const fetchAll = useCallback(async () => {
    const h = getAuthHeaders();
    try {
      const [s, r, c, n, sp, spo, g, b] = await Promise.all([
        axios.get(`${API}/admin/stats`, h),
        axios.get(`${API}/admin/registrations`, h),
        axios.get(`${API}/admin/contacts`, h),
        axios.get(`${API}/admin/newsletter`, h),
        axios.get(`${API}/speakers`),
        axios.get(`${API}/sponsors`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/blog`),
      ]);
      setStats(s.data);
      setRegistrations(r.data);
      setContacts(c.data);
      setNewsletter(n.data);
      setSpeakers(sp.data);
      setSponsors(spo.data);
      setGallery(g.data);
      setBlog(b.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <OverviewTab stats={stats} />;
      case "registrations": return <RegistrationsTab data={registrations} refresh={fetchAll} />;
      case "contacts": return <ContactsTab data={contacts} refresh={fetchAll} />;
      case "newsletter": return <NewsletterTab data={newsletter} refresh={fetchAll} />;
      case "speakers": return <SpeakersTab data={speakers} refresh={fetchAll} />;
      case "sponsors": return <SponsorsTab data={sponsors} refresh={fetchAll} />;
      case "gallery": return <GalleryTab data={gallery} refresh={fetchAll} />;
      case "blog": return <BlogTab data={blog} refresh={fetchAll} />;
      default: return <OverviewTab stats={stats} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-dashboard">
      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-zinc-900 border-r border-zinc-800 flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-sm flex items-center justify-center">
              <span className="text-white text-xs font-bold tracking-tighter">ICT</span>
            </div>
            <div>
              <div className="text-white font-semibold text-sm tracking-tight">ICT Summit</div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Admin Panel</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm ${
                activeTab === tab.id ? "bg-teal-600/10 text-teal-400" : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
              }`}
              data-testid={`sidebar-tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
              {activeTab === tab.id && <ChevronRight size={14} className="ml-auto" />}
            </button>
          ))}
        </nav>

        {/* User Info + Logout */}
        <div className="p-4 border-t border-zinc-800">
          <div className="text-zinc-400 text-xs truncate mb-3">{user?.email}</div>
          <div className="flex gap-2">
            <a href="/" className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 transition-colors" data-testid="back-to-site-btn">
              <ArrowLeft size={12} /> Site
            </a>
            <button onClick={handleLogout} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs text-red-400 hover:text-red-300 bg-zinc-800 hover:bg-zinc-700 transition-colors" data-testid="logout-btn">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#09090B]/90 backdrop-blur-sm border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-zinc-400 hover:text-white" data-testid="sidebar-toggle">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
          </button>
          <h1 className="text-lg font-bold text-white tracking-tight capitalize hidden lg:block">
            {tabs.find((t) => t.id === activeTab)?.label || "Dashboard"}
          </h1>
          <div className="text-xs text-zinc-500">
            Admin: <span className="text-zinc-300">{user?.name || user?.email}</span>
          </div>
        </header>

        {/* Content */}
        <div className="p-6 md:p-8">{renderTab()}</div>
      </main>
    </div>
  );
};

export default AdminDashboard;
